import React, { useState, useEffect } from 'react';
import { Button, Card, Avatar, Pagination, Tag, Space, Image, Progress, message, Empty, Spin } from 'antd';
import { 
  LikeOutlined, 
  HeartOutlined, 
  SmileOutlined, 
  FrownOutlined, 
  MehOutlined,
  FireOutlined,
  PushpinOutlined,
  CalendarOutlined,
  UserOutlined,
  EditOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import { getUserIdAsNumber } from '../utils/cognitoUtils';
import { 
  getNews, 
  toggleReaction, 
  votePoll, 
  getPoll,
  NewsItem, 
  NewsCategory,
  NewsPoll 
} from '../services/api';
import moment from 'moment';
import './NewsSection.css';

interface NewsSectionProps {
  categories: NewsCategory[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  onEditClick: () => void;
  canEdit: boolean;
}

const NewsSection: React.FC<NewsSectionProps> = ({
  categories,
  selectedCategory,
  onCategoryChange,
  onEditClick,
  canEdit
}) => {
  const { user } = useCognitoAuth();
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [reactions, setReactions] = useState<Record<number, Record<string, number>>>({});
  const [userReactions, setUserReactions] = useState<Record<number, string | null>>({});
  const [polls, setPolls] = useState<Record<number, NewsPoll>>({});
  const [userVotes, setUserVotes] = useState<Record<number, string[]>>({});

  const fetchNews = async (page: number = 1) => {
    setLoading(true);
    try {
      const includeNotifications = selectedCategory === 'notificações';
      const response = await getNews({
        category: selectedCategory,
        page,
        per_page: 5,
        include_notifications: includeNotifications
      });
      
      if (response.success) {
        setNewsItems(response.data);
        setTotalPages(response.pagination.pages);
        setCurrentPage(page);
        
        // Load reactions and polls for each news item
        response.data.forEach(async (item: NewsItem) => {
          if (item.reactions) {
            const reactionCounts: Record<string, number> = {};
            item.reactions.forEach((r: any) => {
              reactionCounts[r.reaction_type] = (reactionCounts[r.reaction_type] || 0) + 1;
            });
            setReactions(prev => ({ ...prev, [item.id]: reactionCounts }));
            
            // Find user's reaction
            const userReaction = item.reactions.find((r: any) => r.user_id === getUserIdAsNumber(user));
            setUserReactions(prev => ({ ...prev, [item.id]: userReaction?.reaction_type || null }));
          }
          
          if (item.poll) {
            setPolls(prev => ({ ...prev, [item.id]: item.poll! }));
            // Fetch user's vote
            try {
              const pollResponse = await getPoll(item.id);
              if (pollResponse.success && pollResponse.data.user_vote) {
                setUserVotes(prev => ({ ...prev, [item.id]: pollResponse.data.user_vote || [] }));
              }
            } catch (error) {
              // User hasn't voted yet
            }
          }
        });
      }
    } catch (error) {
      message.error('Erro ao carregar notícias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews(1);
  }, [selectedCategory]);

  const handleReaction = async (newsId: number, reactionType: string) => {
    try {
      const response = await toggleReaction(newsId, reactionType);
      if (response.success) {
        setReactions(prev => ({ ...prev, [newsId]: response.data.reactions }));
        setUserReactions(prev => ({ 
          ...prev, 
          [newsId]: response.data.user_reaction 
        }));
      }
    } catch (error) {
      message.error('Erro ao reagir à notícia');
    }
  };

  const handleVote = async (newsId: number, optionId: string) => {
    try {
      const poll = polls[newsId];
      if (!poll) return;
      
      if (poll.is_closed) {
        message.warning('Esta votação já está fechada');
        return;
      }
      
      const currentVotes = userVotes[newsId] || [];
      let newVotes: string[];
      
      if (poll.allow_multiple_votes) {
        // Toggle option
        if (currentVotes.includes(optionId)) {
          newVotes = currentVotes.filter(id => id !== optionId);
        } else {
          newVotes = [...currentVotes, optionId];
        }
      } else {
        // Single vote - replace
        newVotes = currentVotes.includes(optionId) ? [] : [optionId];
      }
      
      const response = await votePoll(newsId, newVotes);
      if (response.success) {
        setPolls(prev => ({ ...prev, [newsId]: response.data }));
        setUserVotes(prev => ({ ...prev, [newsId]: newVotes }));
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao votar');
    }
  };

  const renderReactions = (newsId: number) => {
    const newsReactions = reactions[newsId] || {};
    const userReaction = userReactions[newsId];
    
    const reactionTypes = [
      { type: 'like', icon: <LikeOutlined />, label: 'Gosto' },
      { type: 'love', icon: <HeartOutlined style={{ color: '#ff4d4f' }} />, label: 'Adoro' },
      { type: 'laugh', icon: <SmileOutlined style={{ color: '#faad14' }} />, label: 'Rir' },
      { type: 'wow', icon: <FireOutlined style={{ color: '#fa8c16' }} />, label: 'Uau' },
      { type: 'sad', icon: <FrownOutlined style={{ color: '#1890ff' }} />, label: 'Triste' },
      { type: 'angry', icon: <MehOutlined style={{ color: '#f5222d' }} />, label: 'Zangado' }
    ];
    
    return (
      <div className="news-reactions">
        {reactionTypes.map(({ type, icon, label }) => {
          const count = newsReactions[type] || 0;
          const isActive = userReaction === type;
          
          return (
            <Button
              key={type}
              type={isActive ? 'primary' : 'text'}
              icon={icon}
              size="small"
              onClick={() => handleReaction(newsId, type)}
              className={`reaction-btn ${isActive ? 'active' : ''}`}
            >
              {count > 0 && <span className="reaction-count">{count}</span>}
            </Button>
          );
        })}
      </div>
    );
  };

  const renderPoll = (newsId: number) => {
    const poll = polls[newsId];
    if (!poll) return null;
    
    const userVote = userVotes[newsId] || [];
    const totalVotes = poll.options.reduce((sum, opt) => sum + (opt.vote_count || 0), 0);
    const canShowResults = poll.show_results_live || poll.is_closed;
    
    return (
      <div className="news-poll">
        <div className="poll-question">{poll.question}</div>
        {poll.is_closed && (
          <Tag color="red" style={{ marginBottom: 12 }}>Votação Fechada</Tag>
        )}
        <div className="poll-options">
          {poll.options.map((option) => {
            const isSelected = userVote.includes(option.id);
            const voteCount = option.vote_count || 0;
            const percentage = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
            
            return (
              <div key={option.id} className="poll-option">
                <Button
                  type={isSelected ? 'primary' : 'default'}
                  block
                  onClick={() => !poll.is_closed && handleVote(newsId, option.id)}
                  disabled={poll.is_closed}
                  style={{ 
                    marginBottom: 8,
                    textAlign: 'left',
                    height: 'auto',
                    padding: '8px 12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{option.text}</span>
                    {canShowResults && (
                      <span style={{ marginLeft: 8, fontWeight: 'bold' }}>
                        {voteCount} ({percentage.toFixed(1)}%)
                      </span>
                    )}
                  </div>
                </Button>
                {canShowResults && (
                  <Progress 
                    percent={percentage} 
                    showInfo={false} 
                    strokeColor={isSelected ? '#1890ff' : '#d9d9d9'}
                    style={{ marginTop: 4 }}
                  />
                )}
              </div>
            );
          })}
        </div>
        {!poll.is_closed && (
          <div className="poll-closes-at">
            <CalendarOutlined /> Fecha em {moment(poll.closes_at).format('DD/MM/YYYY HH:mm')}
          </div>
        )}
      </div>
    );
  };

  // Helper function to convert hex color to rgba
  const hexToRgba = (hex: string, alpha: number): string => {
    if (!hex || !hex.startsWith('#')) {
      // If not a hex color, try to parse it or return default
      return hex || `rgba(24, 144, 255, ${alpha})`;
    }
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const renderNewsItem = (item: NewsItem) => {
    const category = categories.find(c => c.tag === item.category_tag);
    const formattedDate = moment(item.created_at).format('DD/MM/YYYY');
    
    return (
      <Card 
        key={item.id} 
        className={`news-item-card ${item.is_pinned ? 'pinned' : ''}`}
        style={{ marginBottom: 16 }}
      >
        <div className="news-item-header">
          <div className="news-item-meta">
            {item.is_pinned && <PushpinOutlined style={{ color: '#faad14', marginRight: 8 }} />}
            {category && (
              <Tag 
                className="news-category-tag"
                style={{ 
                  borderColor: hexToRgba(category.color || '#1890ff', 0.4),
                  background: `linear-gradient(135deg, ${hexToRgba(category.color || '#1890ff', 0.3)} 0%, ${hexToRgba(category.color || '#1890ff', 0.2)} 100%)`,
                  boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 0 0 1px ${hexToRgba(category.color || '#1890ff', 0.4)}`
                } as React.CSSProperties}
              >
                @{category.display_name}
              </Tag>
            )}
            {item.creator && (
              <span className="news-item-author">
                <UserOutlined style={{ marginRight: 4 }} />
                {item.creator.username}
              </span>
            )}
          </div>
        </div>
        
        <h3 className="news-item-title">{formattedDate} - {item.title}</h3>
        
        {item.description && (
          <div 
            className="news-item-description"
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        )}
        
        {item.images && item.images.length > 0 && (
          <div className="news-item-images">
            <Image.PreviewGroup>
              {item.images.map((img) => (
                <Image
                  key={img.id}
                  src={img.image_url}
                  alt={img.filename || 'News image'}
                  width={200}
                  style={{ marginRight: 8, marginBottom: 8, borderRadius: 8 }}
                />
              ))}
            </Image.PreviewGroup>
          </div>
        )}
        
        {item.poll && renderPoll(item.id)}
        
        {renderReactions(item.id)}
      </Card>
    );
  };

  return (
    <div className="news-section-wrapper">
      {/* Category Tabs */}
      <div className="news-category-tabs">
        <Button
          type={selectedCategory === 'all' ? 'primary' : 'default'}
          onClick={() => onCategoryChange('all')}
          className="news-tab"
        >
          Todas
        </Button>
        {categories.map((category) => {
          const isActive = selectedCategory === category.tag;
          const categoryColor = category.color || '#1890ff';
          return (
            <Button
              key={category.id}
              type={isActive ? 'primary' : 'default'}
              onClick={() => onCategoryChange(category.tag)}
              className="news-tab"
              style={isActive ? {
                borderBottomColor: categoryColor,
                background: `linear-gradient(135deg, ${hexToRgba(categoryColor, 0.4)} 0%, ${hexToRgba(categoryColor, 0.3)} 100%)`,
                boxShadow: `0 8px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 0 0 1px ${hexToRgba(categoryColor, 0.3)}`
              } as React.CSSProperties : {}}
            >
              @{category.display_name}
            </Button>
          );
        })}
      </div>

      {/* Edit Button and Refresh Button */}
      {canEdit && (
        <div className="news-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button 
            type="default" 
            size="small" 
            icon={<ReloadOutlined />} 
            onClick={() => fetchNews(currentPage)}
            className="edit-news-btn"
            title="Atualizar notícias"
          >
            Atualizar
          </Button>
          <Button 
            type="default" 
            size="small" 
            icon={<EditOutlined />} 
            onClick={onEditClick}
            className="edit-news-btn"
          >
            Editar Notícias
          </Button>
        </div>
      )}

      {/* News List */}
      <div className="news-list-container">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px' }}>
            <Spin size="large" />
          </div>
        ) : newsItems.length === 0 ? (
          <Empty 
            description="Nenhuma notícia disponível"
            style={{ padding: '40px' }}
          />
        ) : (
          <>
            {newsItems.map(renderNewsItem)}
            {totalPages > 1 && (
              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Pagination
                  current={currentPage}
                  total={totalPages * 5}
                  pageSize={5}
                  onChange={(page) => fetchNews(page)}
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NewsSection;

